// (c) 2026 Owl Consultations. All rights reserved.
// D-SUPPORT-1 — global recording indicator. Mounted once at the app root so it persists across
// ALL navigation (the whole point: users reproduce the bug by moving between screens). Shows a
// floating "recording" pill everywhere with elapsed time; Stop opens a small dialog to file the
// recording as a support ticket. The recorder engine itself lives at module scope in
// replayRecorder.ts and keeps running regardless of which page is mounted.
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { subscribeRecording, isRecording, recordingStartedAt, maxRecordingMinutes, stopRecording } from '../support/replayRecorder'
import * as api from '../api/client'

export default function RecordingWidget() {
  const [active, setActive] = useState(isRecording())
  const [elapsed, setElapsed] = useState(0)
  const [showStop, setShowStop] = useState(false)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedTicket, setSavedTicket] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => subscribeRecording(() => setActive(isRecording())), [])
  useEffect(() => {
    if (!active) { setElapsed(0); return }
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - recordingStartedAt()) / 1000)), 500)
    return () => clearInterval(id)
  }, [active])

  if (!active && !showStop && !savedTicket) return null

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0')
  const ss = String(elapsed % 60).padStart(2, '0')
  const nearCap = elapsed > (maxRecordingMinutes() * 60 - 60)

  const pill: React.CSSProperties = {
    position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
    display: 'flex', alignItems: 'center', gap: 10,
    background: '#111827', color: '#fff', padding: '10px 14px', borderRadius: 999,
    boxShadow: '0 6px 24px rgba(0,0,0,0.28)', fontSize: 14,
  }
  const dot: React.CSSProperties = {
    width: 11, height: 11, borderRadius: 999, background: '#ef4444',
    animation: 'replayPulse 1.2s ease-in-out infinite',
  }

  const doStop = async () => {
    setShowStop(true)
  }

  const fileTicket = async () => {
    setSaving(true)
    try {
      const subject = note.trim() ? note.trim().slice(0, 80) : 'Recorded issue'
      const ticket = await api.createSupportTicket({
        subject,
        body: note.trim() || 'User recorded a session showing this issue. See the attached replay.',
        category: 'technical',
        severity: 'normal',
      })
      await stopRecording(ticket.id, subject)
      setShowStop(false); setSavedTicket(ticket.id); setNote('')
    } catch {
      // If ticket creation fails, still stop the recording so nothing dangles.
      await stopRecording(null)
      setShowStop(false)
    } finally { setSaving(false) }
  }

  const discard = async () => {
    await stopRecording(null)   // completes without a ticket; retention purges unattached at 7 days
    setShowStop(false); setNote('')
  }

  return (
    <>
      <style>{`@keyframes replayPulse{0%,100%{opacity:1}50%{opacity:.35}}`}</style>

      {active && !showStop && (
        <div style={pill} role="status" aria-live="polite">
          <span style={dot} />
          <span>Recording {mm}:{ss}{nearCap ? ' · ending soon' : ''}</span>
          <button onClick={doStop}
            style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 999, padding: '5px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            ■ Stop
          </button>
        </div>
      )}

      {showStop && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)', borderRadius: 14, padding: 20, width: 'min(440px, 100%)', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6 }}>Stop recording & send to support</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-mute, #667085)', marginBottom: 12 }}>
              Briefly, what went wrong? This creates a support ticket with your recording attached.
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder="e.g. The Save button did nothing on the patient screen"
              style={{ width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid var(--color-border, #d0d5dd)', padding: 10, fontSize: 14, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={discard} disabled={saving}
                style={{ background: 'transparent', border: '1px solid var(--color-border, #d0d5dd)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>
                Discard
              </button>
              <button onClick={fileTicket} disabled={saving}
                style={{ background: '#0F766E', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Sending…' : 'Send to support'}
              </button>
            </div>
          </div>
        </div>
      )}

      {savedTicket && (
        <div style={pill}>
          <span>✓ Recording sent</span>
          <button onClick={() => { const t = savedTicket; setSavedTicket(null); navigate(`/support/${t}`) }}
            style={{ background: '#0F766E', color: '#fff', border: 'none', borderRadius: 999, padding: '5px 12px', fontSize: 13, cursor: 'pointer' }}>
            View ticket
          </button>
          <button onClick={() => setSavedTicket(null)}
            style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 999, padding: '5px 10px', fontSize: 13, cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      )}
    </>
  )
}
